import { immerable } from 'immer';
import { freeze } from 'immutable-less/immer';
import { cloneDeep } from 'immutable-less/internal/cloneDeep';
import {
  BaseImmutableObject,
  BaseImmutableObjectAbstractMethods,
  ImmutableObject as _ImmutableObject,
  GetJSONType,
  GetRecordType,
} from 'immutable-less/BaseImmutableObject';
import { produce } from 'immutable-less/internal/produce';

type SerializationOptions<JSONType, RecordType> = {
  fromJSON: (json: JSONType) => RecordType;
  toJSON: (record: ImmutableObject<JSONType, RecordType>) => JSONType;
};

export function createImmutableObjectClass<JSONType>(
  defaults: JSONType
): ImmutableObjectConstructor<JSONType, JSONType>;

export function createImmutableObjectClass<JSONType, RecordType = JSONType>(
  defaults: JSONType,
  serializationOptions: SerializationOptions<JSONType, RecordType>
): ImmutableObjectConstructor<JSONType, RecordType>;

export function createImmutableObjectClass<
  JSONType extends object,
  RecordType = JSONType,
>(
  defaults: JSONType,
  serializationOptions?: SerializationOptions<JSONType, RecordType>
): ImmutableObjectConstructor<JSONType, RecordType> {
  class ImmutableObjectImplementation
    extends BaseImmutableObject<JSONType, RecordType>
    implements BaseImmutableObjectAbstractMethods<JSONType, RecordType>
  {
    constructor(partial?: Partial<JSONType> | null) {
      super();
      const jsonValue = { ...defaults };

      if (partial) {
        Object.keys(defaults).forEach(((key: keyof JSONType) => {
          if (Object.hasOwnProperty.call(partial, key)) {
            jsonValue[key] = partial[key]!;
          }
        }) as (key: string) => void);
      }

      const recordValue = serializationOptions
        ? serializationOptions.fromJSON(jsonValue)
        : (jsonValue as unknown as RecordType); // only when RecordType = JSONType, enforced using the different overrides for ImmutableObject

      Object.assign(this, recordValue);

      freeze(this, true);
    }

    toJSON<
      DerivedClass extends ImmutableObject<JSONType, RecordType>,
      DerivedJSONType = GetJSONType<DerivedClass>,
    >(this: DerivedClass): DerivedJSONType {
      if (serializationOptions) {
        return serializationOptions.toJSON(this) as unknown as DerivedJSONType; // this type is already enforced when ImmutableObject() is called
      } else {
        return cloneDeep({ ...this } as unknown as DerivedJSONType); // only when RecordType = JSONType, enforced using the different overrides for ImmutableObject;
      }
    }

    clear(): this {
      return produce(this, (draft) => {
        Object.assign(draft, new ImmutableObjectImplementation());
      });
    }
  }
  Object.defineProperty(ImmutableObjectImplementation, immerable, {
    value: true,
  });
  return ImmutableObjectImplementation as ImmutableObjectConstructor<
    JSONType,
    RecordType
  >;
}

export interface ImmutableObjectConstructor<JSONType, RecordType = JSONType> {
  new (
    partial?: Partial<JSONType> | null
  ): ImmutableObject<JSONType, RecordType>;
}

export type ImmutableObject<JSONType, RecordType = JSONType> = _ImmutableObject<
  JSONType,
  RecordType
>;
export type JSONType<T> = GetJSONType<T>;
export type RecordType<T> = GetRecordType<T>;
